import React, { ReactNode, useEffect, useState } from "react";
import { usePermissions } from "@/contexts/PermissionContext";
import { useAuthStore } from "@/stores/auth/auth-store";
import {
  ResourceType,
  PermissionAction,
  SystemRole,
  ProjectRole,
  ComponentRole,
} from "@/types/permissions";
import { Loader2, Lock } from "lucide-react";

interface PermissionGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
  // Resource-based check
  resource?: {
    type: ResourceType;
    id: string;
  };
  action?: PermissionAction;
  // Role-based check
  requireSystemRole?: SystemRole | SystemRole[];
  requireProjectRole?: ProjectRole | ProjectRole[];
  requireComponentRole?: ComponentRole | ComponentRole[];
  // Additional options
  requireAll?: boolean; // For multiple role requirements
  showError?: boolean;
  onDenied?: () => void;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  fallback,
  loading,
  resource,
  action,
  requireSystemRole,
  requireProjectRole,
  requireComponentRole,
  requireAll = false,
  showError = false,
  onDenied,
}) => {
  // Add safe context access with error boundary
  let permissionContext;
  try {
    permissionContext = usePermissions();
  } catch (error) {
    console.error('PermissionGuard: Permission context not available, falling back to restricted access:', error);
    // Return fallback if context is not available
    return <>{fallback}</>;
  }

  const {
    checkPermission,
    systemRole,
    projectRoles,
    componentRoles,
    isLoading: contextLoading,
  } = permissionContext;

  const { user } = useAuthStore();

  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthorization = async () => {
      setIsChecking(true);
      setError(null);

      try {
        const checks: boolean[] = [];

        // Resource-based permission check
        if (resource && action) {
          const result = await checkPermission({ resource, action });
          checks.push(result.allowed);
          if (!result.allowed && result.reason) {
            setError(result.reason);
          }
        }

        // System role check
        if (requireSystemRole) {
          const requiredRoles = Array.isArray(requireSystemRole)
            ? requireSystemRole
            : [requireSystemRole];
          const hasRole = requiredRoles.includes(systemRole);
          checks.push(hasRole);
          if (!hasRole) {
            setError(`Required system role: ${requiredRoles.join(" or ")}`);
          }
        }

        // Project role check (checks all projects)
        if (requireProjectRole) {
          const requiredRoles = Array.isArray(requireProjectRole)
            ? requireProjectRole
            : [requireProjectRole];
          const hasRole = Array.from(projectRoles.values()).some((role) =>
            requiredRoles.includes(role),
          );
          checks.push(hasRole);
          if (!hasRole) {
            setError(`Required project role: ${requiredRoles.join(" or ")}`);
          }
        }

        // Component role check (checks all components)
        if (requireComponentRole) {
          const requiredRoles = Array.isArray(requireComponentRole)
            ? requireComponentRole
            : [requireComponentRole];
          const hasRole = Array.from(componentRoles.values()).some((role) =>
            requiredRoles.includes(role),
          );
          checks.push(hasRole);
          if (!hasRole) {
            setError(`Required component role: ${requiredRoles.join(" or ")}`);
          }
        }

        // Determine authorization based on requireAll flag
        const authorized =
          checks.length === 0 ||
          (requireAll ? checks.every(Boolean) : checks.some(Boolean));

        setIsAuthorized(authorized);

        if (!authorized && onDenied) {
          onDenied();
        }
      } catch (err) {
        console.error("Permission check failed:", err);
        setError("Permission check failed");
        setIsAuthorized(false);
        if (onDenied) {
          onDenied();
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkAuthorization();
  }, [
    resource?.type,
    resource?.id,
    action,
    requireSystemRole,
    requireProjectRole,
    requireComponentRole,
    requireAll,
    systemRole,
    projectRoles,
    componentRoles,
    onDenied,
  ]);

  // Show loading state
  if (contextLoading || isChecking || isAuthorized === null) {
    if (loading) {
      return <>{loading}</>;
    }
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not authorized
  if (!isAuthorized) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Lock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {error || "You do not have permission to access this resource."}
          </p>
        </div>
      );
    }

    return null;
  }

  // Authorized
  return <>{children}</>;
};

// Convenience components for common permission checks
export const SystemAdminOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard
    requireSystemRole={SystemRole.SUPER_ADMIN}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
);

export const OrganizationAdminOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard
    requireSystemRole={[SystemRole.SUPER_ADMIN, SystemRole.ORGANIZATION_ADMIN]}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
);

export const DesignerOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard
    requireSystemRole={[
      SystemRole.SUPER_ADMIN,
      SystemRole.ORGANIZATION_ADMIN,
      SystemRole.DESIGNER,
    ]}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
);

export const ProjectEditorOnly: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard
    requireProjectRole={[
      ProjectRole.OWNER,
      ProjectRole.ADMIN,
      ProjectRole.EDITOR,
    ]}
    fallback={fallback}
  >
    {children}
  </PermissionGuard>
);

// Component for conditional rendering based on permissions
export const CanAccess: React.FC<{
  resource: { type: ResourceType; id: string };
  action: PermissionAction;
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ resource, action, children, fallback }) => (
  <PermissionGuard resource={resource} action={action} fallback={fallback}>
    {children}
  </PermissionGuard>
);

// Project-specific guard
export const ProjectGuard: React.FC<{
  projectId: string;
  action: PermissionAction;
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
  showError?: boolean;
  onDenied?: () => void;
}> = ({
  projectId,
  action,
  children,
  fallback,
  loading,
  showError,
  onDenied,
}) => (
  <PermissionGuard
    resource={{ type: ResourceType.PROJECT, id: projectId }}
    action={action}
    fallback={fallback}
    loading={loading}
    showError={showError}
    onDenied={onDenied}
  >
    {children}
  </PermissionGuard>
);
